// Shared helpers for the Users analysis page — roll up cached login /
// message / referral records into per-user profiles without a dedicated
// backend endpoint (v1 uses existing /api/cache/data).

import { unwrapDateLike } from './dateUtils';

const f = (r, k) => (r?.data || r?.Data || {})[k];

export const LOGIN_FIELDS = [
  'web_login', 'goweb_login', 'common_login',
  'desktop_login', 'mobile', 'telemedicine_login',
];

export const MESSAGE_SENT_FIELDS = [
  'referral_messages_sent', 'vmail_messages_sent', 'vmail_messages_Sent',
  'vchat_messages_sent', 'vchat_messages_Sent',
  'vround_messages_sent', 'vtransfer_messages_sent', 'Other_messages_sent',
];

export const DEVICE_REP_TYPE_RX =
  /medical\s*device|device\s*rep|vendor|equipment\s*rep|industry\s*rep|mdr/i;

/** Normalize any cached created_date / timestamp to YYYY-MM-DD. */
export function recordDateYmd(record) {
  const raw = f(record, 'created_date') || record?.timestamp;
  const unwrapped = unwrapDateLike(raw);
  if (unwrapped == null || unwrapped === '') return '';

  if (typeof unwrapped === 'string') {
    const isoDay = unwrapped.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDay) return isoDay[1];
    const slash = unwrapped.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slash) {
      return `${slash[3]}-${String(slash[2]).padStart(2, '0')}-${String(slash[1]).padStart(2, '0')}`;
    }
  }

  const d = unwrapped instanceof Date ? unwrapped : new Date(unwrapped);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  return '';
}

export function recordDate(record) {
  return recordDateYmd(record);
}

export function sumFields(record, fields) {
  let total = 0;
  const data = record?.data || record?.Data || {};
  for (const key of fields) {
    const v = Number(data[key]);
    if (Number.isFinite(v)) total += v;
  }
  return total;
}

export function pickInt(record, ...keys) {
  const data = record?.data || record?.Data || {};
  for (const k of keys) {
    const v = data[k];
    if (v !== undefined && v !== null && v !== '') return Number(v);
  }
  return 0;
}

export function totalLogins(record) {
  return sumFields(record, LOGIN_FIELDS);
}

export function totalMessagesSent(record) {
  return sumFields(record, MESSAGE_SENT_FIELDS);
}

export function isDeviceRepUserType(userType) {
  if (!userType) return false;
  return DEVICE_REP_TYPE_RX.test(String(userType));
}

export function filterLoginRow(row, { facIds, removeTestData, removeVeUsers, isTestRecord, isVeRow }) {
  if (removeTestData && isTestRecord?.(row)) return false;
  if (removeVeUsers && isVeRow?.(row)) return false;
  if (!facIds || facIds.size === 0) return true;
  const facId = f(row, 'facility_id');
  return facId != null && facIds.has(String(facId));
}

export function filterMessageRow(row, opts) {
  return filterLoginRow(row, opts);
}

export function filterReferralRow(row, { facIds, removeTestData, removeVeUsers, isTestRecord, isVeRow }) {
  if (removeTestData && isTestRecord?.(row)) return false;
  if (removeVeUsers && isVeRow?.(row)) return false;
  if (!facIds || facIds.size === 0) return true;
  const recv = f(row, 'receiving_facility_id');
  const send = f(row, 'sending_facility_id');
  return (recv != null && facIds.has(String(recv)))
    || (send != null && facIds.has(String(send)));
}

function emptyProfile(userId) {
  return {
    userId: String(userId),
    name: '',
    userType: '',
    speciality: '',
    homeFacilityId: '',
    homeFacility: '',
    homeCity: '',
    homeState: '',
    userCity: '',
    userState: '',
    logins: 0,
    loginDays: new Set(),
    web: 0,
    mobile: 0,
    desktop: 0,
    goweb: 0,
    telemedicine: 0,
    common: 0,
    messagesSent: 0,
    referralsSent: 0,
    referralsReceived: 0,
    collaboratingFacilities: new Map(), // id -> name
    lastActiveDate: '',
  };
}

function touchProfile(profile, sample) {
  if (!profile.name && sample.name) profile.name = sample.name;
  if (!profile.userType && sample.userType) profile.userType = sample.userType;
  if (!profile.speciality && sample.speciality) profile.speciality = sample.speciality;
  if (!profile.homeFacility && sample.facility) profile.homeFacility = sample.facility;
  if (!profile.homeFacilityId && sample.facilityId) profile.homeFacilityId = String(sample.facilityId);
  if (!profile.homeCity && sample.facilityCity) profile.homeCity = sample.facilityCity;
  if (!profile.homeState && sample.facilityState) profile.homeState = sample.facilityState;
  if (!profile.userCity && sample.userCity) profile.userCity = sample.userCity;
  if (!profile.userState && sample.userState) profile.userState = sample.userState;
}

function addCollabFacility(profile, facId, facName) {
  const name = (facName || '').trim();
  const homeName = (profile.homeFacility || '').trim();
  if (name && homeName && name.toLowerCase() === homeName.toLowerCase()) return;

  const idKey = facId != null && facId !== '' ? String(facId) : null;
  if (idKey && profile.homeFacilityId && idKey === String(profile.homeFacilityId)) return;

  const key = idKey || name;
  if (!key) return;
  profile.collaboratingFacilities.set(key, name || idKey);
}

export function buildUserProfiles(loginRows, messageRows = [], referralRows = []) {
  const map = new Map();

  const ensure = (userId, sample = {}) => {
    const key = String(userId);
    if (!map.has(key)) map.set(key, emptyProfile(userId));
    const p = map.get(key);
    touchProfile(p, sample);
    return p;
  };

  for (const r of loginRows) {
    const userId = pickInt(r, 'user_id');
    if (!userId) continue;
    const day = recordDate(r);
    const n = totalLogins(r);
    if (n <= 0) continue;
    const p = ensure(userId, {
      name: f(r, 'user_display_name'),
      userType: f(r, 'user_type'),
      speciality: f(r, 'speciality'),
      facility: f(r, 'facility_name'),
      facilityId: f(r, 'facility_id'),
      facilityCity: f(r, 'facility_physical_city'),
      facilityState: f(r, 'facility_physical_state'),
      userCity: f(r, 'user_physical_city'),
      userState: f(r, 'user_physical_state'),
    });
    p.logins += n;
    if (day) {
      p.loginDays.add(day);
      if (!p.lastActiveDate || day > p.lastActiveDate) p.lastActiveDate = day;
    }
    p.web += Number(f(r, 'web_login')) || 0;
    p.mobile += Number(f(r, 'mobile')) || 0;
    p.desktop += Number(f(r, 'desktop_login')) || 0;
    p.goweb += Number(f(r, 'goweb_login')) || 0;
    p.telemedicine += Number(f(r, 'telemedicine_login')) || 0;
    p.common += Number(f(r, 'common_login')) || 0;
    // Reps often log in under customer hospital facilities — track those too.
    addCollabFacility(p, f(r, 'facility_id'), f(r, 'facility_name'));
  }

  for (const r of messageRows) {
    const userId = pickInt(r, 'user_id');
    if (!userId) continue;
    const sent = totalMessagesSent(r);
    if (sent <= 0) continue;
    const p = ensure(userId, {
      name: f(r, 'user_display_name'),
      userType: f(r, 'user_type'),
      speciality: f(r, 'speciality'),
      facility: f(r, 'facility_name'),
      facilityId: f(r, 'facility_id'),
      userCity: f(r, 'user_physical_city'),
      userState: f(r, 'user_physical_state'),
    });
    p.messagesSent += sent;
    const day = recordDate(r);
    if (day && (!p.lastActiveDate || day > p.lastActiveDate)) p.lastActiveDate = day;
    addCollabFacility(p, f(r, 'facility_id'), f(r, 'facility_name'));
  }

  for (const r of referralRows) {
    const sId = pickInt(r, 'sending_user_id');
    const rId = pickInt(r, 'receiving_user_id');
    const day = recordDate(r);
    if (sId) {
      const p = ensure(sId, {
        name: f(r, 'sending_user_name'),
        userType: f(r, 'user_type'),
        speciality: f(r, 'sending_speciality'),
        facility: f(r, 'sending_facility'),
        facilityId: f(r, 'sending_facility_id'),
      });
      p.referralsSent += 1;
      if (day && (!p.lastActiveDate || day > p.lastActiveDate)) p.lastActiveDate = day;
      addCollabFacility(p, f(r, 'receiving_facility_id'), f(r, 'receiving_facility'));
    }
    if (rId) {
      const p = ensure(rId, {
        name: f(r, 'receiving_user_name'),
        userType: f(r, 'user_type'),
        speciality: f(r, 'receiving_speciality'),
        facility: f(r, 'receiving_facility'),
        facilityId: f(r, 'receiving_facility_id'),
      });
      p.referralsReceived += 1;
      if (day && (!p.lastActiveDate || day > p.lastActiveDate)) p.lastActiveDate = day;
      addCollabFacility(p, f(r, 'sending_facility_id'), f(r, 'sending_facility'));
    }
  }

  return map;
}

export function profilesToArray(profileMap) {
  return [...profileMap.values()].map(p => ({
    ...p,
    loginDays: p.loginDays.size,
    collaboratingFacilities: [...p.collaboratingFacilities.values()],
    collaboratingCount: p.collaboratingFacilities.size,
    referralTouches: p.referralsSent + p.referralsReceived,
    totalActivity: p.logins + p.messagesSent + p.referralsSent + p.referralsReceived,
  }));
}

export function dailyLoginRows(loginRows, dayYmd, { userTypeFilter = '' } = {}) {
  const typeNeedle = userTypeFilter.trim().toLowerCase();
  const byUser = new Map();
  for (const r of loginRows) {
    if (recordDateYmd(r) !== dayYmd) continue;
    if (typeNeedle && !(f(r, 'user_type') || '').toLowerCase().includes(typeNeedle)) continue;
    const n = totalLogins(r);
    if (n <= 0) continue;
    const userId = pickInt(r, 'user_id');
    if (!userId) continue;
    const key = String(userId);
    if (!byUser.has(key)) {
      byUser.set(key, {
        userId: key,
        name: f(r, 'user_display_name') || `User ${key}`,
        userType: f(r, 'user_type') || '—',
        facility: f(r, 'facility_name') || '—',
        facilityId: f(r, 'facility_id'),
        speciality: f(r, 'speciality') || '',
        city: f(r, 'user_physical_city') || f(r, 'facility_physical_city') || '',
        state: f(r, 'user_physical_state') || f(r, 'facility_physical_state') || '',
        logins: 0,
        web: 0,
        mobile: 0,
        desktop: 0,
        goweb: 0,
        telemedicine: 0,
        common: 0,
      });
    }
    const row = byUser.get(key);
    row.logins += n;
    row.web += Number(f(r, 'web_login')) || 0;
    row.mobile += Number(f(r, 'mobile')) || 0;
    row.desktop += Number(f(r, 'desktop_login')) || 0;
    row.goweb += Number(f(r, 'goweb_login')) || 0;
    row.telemedicine += Number(f(r, 'telemedicine_login')) || 0;
    row.common += Number(f(r, 'common_login')) || 0;
  }
  return [...byUser.values()].sort((a, b) => b.logins - a.logins || a.name.localeCompare(b.name));
}

export function userTypeBreakdown(profiles) {
  const m = new Map();
  for (const p of profiles) {
    const t = p.userType?.trim() || '(unknown)';
    const cur = m.get(t) || { userType: t, users: 0, logins: 0, messages: 0, activity: 0 };
    cur.users += 1;
    cur.logins += p.logins;
    cur.messages += p.messagesSent;
    cur.activity += p.totalActivity;
    m.set(t, cur);
  }
  return [...m.values()].sort((a, b) => b.users - a.users);
}

export function mapLocationsFromProfiles(profiles) {
  const m = new Map();
  for (const p of profiles) {
    if (p.logins <= 0 && p.messagesSent <= 0) continue;
    const city = p.userCity || p.homeCity;
    const state = p.userState || p.homeState;
    if (!city && !state) continue;
    const key = `${city || ''}|${state || ''}`.toLowerCase();
    const cur = m.get(key) || {
      key,
      city: city || '',
      state: state || '',
      users: new Set(),
      logins: 0,
    };
    cur.users.add(p.userId);
    cur.logins += p.logins;
    m.set(key, cur);
  }
  return [...m.values()]
    .map(loc => ({
      ...loc,
      userCount: loc.users.size,
      users: undefined,
    }))
    .sort((a, b) => b.userCount - a.userCount);
}

export function loginDateRange(loginRows) {
  let min = '';
  let max = '';
  for (const r of loginRows) {
    const day = recordDateYmd(r);
    if (!day) continue;
    if (!min || day < min) min = day;
    if (!max || day > max) max = day;
  }
  return { min, max };
}

/**
 * Daily login trend for a closed [startYmd, endYmd] window.
 * Returns one point per calendar day (zeros filled) with total login
 * count and unique user count. Optional userTypeFilter matches
 * dailyLoginRows.
 */
export function dailyLoginTrend(loginRows, startYmd, endYmd, { userTypeFilter = '' } = {}) {
  if (!startYmd || !endYmd || startYmd > endYmd) return [];

  const typeNeedle = userTypeFilter.trim().toLowerCase();
  const byDay = new Map();

  // Seed every day in the window so the chart doesn't skip quiet days.
  {
    const cur = new Date(`${startYmd}T00:00:00`);
    const end = new Date(`${endYmd}T00:00:00`);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      const key = `${y}-${m}-${d}`;
      byDay.set(key, { day: key, logins: 0, users: new Set() });
      cur.setDate(cur.getDate() + 1);
    }
  }

  for (const r of loginRows) {
    const day = recordDateYmd(r);
    if (!day || day < startYmd || day > endYmd) continue;
    if (typeNeedle && !(f(r, 'user_type') || '').toLowerCase().includes(typeNeedle)) continue;
    const n = totalLogins(r);
    if (n <= 0) continue;
    const userId = pickInt(r, 'user_id');
    const bucket = byDay.get(day);
    if (!bucket) continue;
    bucket.logins += n;
    if (userId) bucket.users.add(String(userId));
  }

  return [...byDay.values()].map(b => ({
    day: b.day,
    logins: b.logins,
    uniqueUsers: b.users.size,
  }));
}

/** Inclusive YMD window ending on `endYmd` (or max loaded), spanning up to `days` days, clamped to loaded data. */
export function trendWindowEnding(loginRows, endYmd, days = 30) {
  const { min, max } = loginDateRange(loginRows);
  if (!min || !max) return { start: '', end: '' };
  const end = endYmd && endYmd >= min && endYmd <= max ? endYmd : max;
  const endDate = new Date(`${end}T00:00:00`);
  endDate.setDate(endDate.getDate() - (Math.max(1, days) - 1));
  const y = endDate.getFullYear();
  const m = String(endDate.getMonth() + 1).padStart(2, '0');
  const d = String(endDate.getDate()).padStart(2, '0');
  let start = `${y}-${m}-${d}`;
  if (start < min) start = min;
  if (start > end) start = end;
  return { start, end };
}

export function sortRows(rows, sortKey, sortDir) {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let av = a[sortKey];
    let bv = b[sortKey];
    if (Array.isArray(av)) av = av.length;
    if (Array.isArray(bv)) bv = bv.length;
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av == null) av = '';
    if (bv == null) bv = '';
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}
export function yesterdayYmd() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function yearStartYmd() {
  const y = new Date().getFullYear();
  return `${y}-01-01`;
}
